import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Zap, ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import StoryCard from './StoryCard';

export default function LatestStoriesSection({ articles, loading, t }) {
  const [newsPage, setNewsPage] = useState(0);
  const NEWS_PER_PAGE = 9;

  const totalNewsPages = Math.max(1, Math.ceil(articles.length / NEWS_PER_PAGE));
  const feedArticles = articles.slice(newsPage * NEWS_PER_PAGE, (newsPage + 1) * NEWS_PER_PAGE);

  return (
    <section data-testid="latest-stories">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-5">
        <h2 className="font-serif text-xl sm:text-2xl font-bold text-navy dark:text-white flex items-center gap-2">
          <Zap size={18} className="text-sky-accent flex-shrink-0" />
          {t('latest_news')}
        </h2>
        <div className="flex items-center gap-2">
          <Link to="/category/news" className="text-sky-accent text-xs font-display font-semibold hover:text-sky-accent-dark flex items-center gap-1 whitespace-nowrap">
            {t('view_all')} <ArrowRight size={13} />
          </Link>
        </div>
      </div>
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1,2,3,4,5,6].map(i => <div key={i} className="skeleton aspect-[4/3] rounded-2xl" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="latest-stories-grid">
          {feedArticles.map((a, i) => (
            <StoryCard key={a.id || a._id || a.slug} article={a} featured={i === 0 && newsPage === 0} />
          ))}
        </div>
      )}
      {/* Pagination */}
      {totalNewsPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-6" data-testid="latest-stories-pagination">
          <button
            onClick={() => setNewsPage(p => Math.max(0, p - 1))}
            disabled={newsPage === 0}
            className="p-2 rounded-lg border border-gray-200 dark:border-white/10 hover:bg-gray-50 dark:hover:bg-white/5 disabled:opacity-30 transition-colors"
            data-testid="news-prev-page"
          >
            <ChevronLeft size={16} />
          </button>
          <div className="flex items-center gap-1">
            {Array.from({ length: totalNewsPages }).map((_, i) => {
              // Show max 7 page buttons with ellipsis
              if (totalNewsPages <= 7 || i === 0 || i === totalNewsPages - 1 || Math.abs(i - newsPage) <= 1) {
                return (
                  <button
                    key={i}
                    onClick={() => setNewsPage(i)}
                    className={`min-w-[32px] h-8 rounded-lg text-xs font-display font-semibold transition-all ${
                      i === newsPage
                        ? 'bg-sky-accent text-white shadow-sm'
                        : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/5'
                    }`}
                    data-testid={`news-page-btn-${i}`}
                  >
                    {i + 1}
                  </button>
                );
              }
              if (i === 1 && newsPage > 3) return <span key={i} className="px-1 text-gray-400 text-xs">...</span>;
              if (i === totalNewsPages - 2 && newsPage < totalNewsPages - 4) return <span key={i} className="px-1 text-gray-400 text-xs">...</span>;
              return null;
            })}
          </div>
          <button
            onClick={() => setNewsPage(p => Math.min(totalNewsPages - 1, p + 1))}
            disabled={newsPage === totalNewsPages - 1}
            className="p-2 rounded-lg border border-gray-200 dark:border-white/10 hover:bg-gray-50 dark:hover:bg-white/5 disabled:opacity-30 transition-colors"
            data-testid="news-next-page"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      )}
    </section>
  );
}
